import domtoimage from 'dom-to-image';
import { QRCodeSVG } from 'qrcode.react';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { NOTIFICATION_TYPES, SharePostLinksEnum } from 'src/common/constants';
import { copyToClipboard, currencyFormatter } from 'src/common/helpers';
import { Modal, ModalContent, ModalWithDrawer, Drawer } from 'src/components/core';
import {
    Cancel,
    CopyLink,
    Download,
    Email,
    Facebook,
    Linkedin,
    PnLLoss,
    PnLProfit,
    Telegram,
    Twitter,
    Whatsapp,
} from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { BASE_URL, COINFLARE_REGISTER_URL } from 'src/config';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { showNotification } from 'src/setup/redux/slices/derivativesSlice';
import { ToastType, Position } from 'src/types';

interface ShareProfitLossModalProps {
    open: boolean;
    position: Position;
    isActive: boolean;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    height: max-content;
    margin-bottom: 250px;
    .modal-paper {
        width: 343px;
    }
    .modal-content {
        padding: 0px;
        overflow: hidden;
    }
`;

const ImageContainer = styled.div`
    width: 343px;
    height: 429px;
    position: relative;
`;

const PnLAnalysisContainer = styled(FlexContainer)`
    position: absolute;
    top: 53px;
    left: 19px;
`;

const TokenName = styled.span`
    ${({ theme: { colors, typography } }) => `
        font-weight: ${typography.weight.medium};
        line-height: ${typography.lineHeight[7]};
        color: ${colors.text.primary};
    `}
`;

const RoiContainer = styled(FlexContainer)`
    gap: 12.5px;
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[3]};
        color: ${colors.text.tertiary};
    `}
`;

const PositionSide = styled.span<{ isLong: number }>`
    padding: 4px 8px;
    border-radius: 25px;
    ${({ theme: { colors }, isLong }) => `
        background: ${
            isLong ? colors.palette.background.overlay.green : colors.palette.background.overlay.red
        };
        color: ${isLong ? colors.alert.green : colors.alert.red};
    `}
`;

const PositionLeverage = styled.span`
    ${({ theme: { colors } }) => `
        color: ${colors.alert.yellow};
    `}
`;

const PositionPnLPercentage = styled.span<{ color: string }>`
    ${({ theme: { typography }, color }) => `
        color: ${color};
        font-size: ${typography.size.desktop.heading.h3};
        line-height: ${typography.lineHeight[14]};
        font-weight: ${typography.weight.medium};
    `}
`;

const PricesContainer = styled(FlexContainer)`
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    & span {
        ${({ theme: { colors, typography } }) => `
            &:nth-child(1) {
                color: ${colors.text.tertiary};
                font-size: ${typography.size.desktop.paragraph.p3};
                letter-spacing: 0.2px;
            }
            &:nth-child(2) {
                color: ${colors.palette.white};
                font-size: ${typography.size.desktop.paragraph.p2};
                line-height: ${typography.lineHeight[5]};
                font-weight: ${typography.weight.medium};
            }
        `}
    }
`;

const ReferralContainer = styled(FlexContainer)`
    position: absolute;
    left: 16px;
    bottom: 7px;
`;

const ReferralDescription = styled.span`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.accent};
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: ${typography.lineHeight[3]};
    `}
`;

const ReferralCode = styled.span`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.accent};
        font-size: ${typography.size.desktop.paragraph.p2};
        line-height: ${typography.lineHeight[5]};
        font-weight: ${typography.weight.semiBold};
    `}
`;

const DrawerContainer = styled(FlexContainer)`
    ${({ theme: { colors } }) => `
        background-color: ${colors.background.secondary.v3};
    `}
    flex-direction: column;
    padding: 30px 16px 60px;
    border-radius: 20px 20px 0px 0px;
    gap: 20px;
    width: 100%;
`;

const DrawerHeader = styled(FlexContainer)`
    justify-content: space-between;
    width: 100%;
`;

const DrawerTitle = styled.div`
    ${({ theme: { typography, colors } }) => `
        color: ${colors.text.primary};
        font-size: ${typography.size.mobile.heading.h6};
        font-weight: ${typography.weight.semiBold};
    `}
`;

const SocialMediaContainer = styled(FlexContainer)`
    align-self: flex-start;
    gap: 10.75px;
    width: 100%;
    overflow-x: auto;
    flex-wrap: nowrap;
    -ms-overflow-style: none;
    scrollbar-width: none;
    ::-webkit-scrollbar {
        display: none;
    }
`;

const SocialMediaItem = styled(FlexContainer)`
    flex-direction: column;
    gap: 2px;
    text-align: center;
    width: 60px;
    cursor: pointer;
    flex: 0 0 auto;
`;

const SocialMediaItemText = styled.div`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.tertiary};
        font-size: ${typography.size.mobile.paragraph.p3};
        width: 100%;
    `}
`;

const ModalWithDrawerMobile: FC<ShareProfitLossModalProps> = ({
    open,
    position,
    isActive,
    handleClose,
}) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const ref = useRef<HTMLDivElement | null>(null);

    const dispatch = useAppDispatch();

    const { userInviteInfo } = useAppSelector((state) => state.user);

    const onSaveImageClick = useCallback(() => {
        if (ref.current === null) {
            return;
        }

        domtoimage
            .toPng(ref.current, { cacheBust: true })
            .then((dataUrl) => {
                const link = document.createElement('a');
                link.download = 'my-pnl.png';
                link.href = dataUrl;
                link.click();
            })
            .catch((err) => {
                console.log(err);
            });
    }, [ref]);

    const Modal = useMemo(() => {
        return (
            <StyledModal size="md" open={open} isBackdropHidden>
                <ModalContent>
                    <ImageContainer ref={ref}>
                        {Number(position.profitPercentage) > 0 ? <PnLProfit /> : <PnLLoss />}
                        <PnLAnalysisContainer>
                            <FlexContainer
                                styles={{
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                }}
                            >
                                <TokenName>{position.symbolName}</TokenName>
                                <RoiContainer>
                                    <span>{t('roi')}</span>
                                    <PositionSide isLong={Number(position.isLong)}>
                                        {t(Number(position.isLong) ? 'long' : 'short')}
                                    </PositionSide>
                                    <PositionLeverage>{`${t('leverage')} ${
                                        position.leverage
                                    }x`}</PositionLeverage>
                                </RoiContainer>
                                <FlexContainer
                                    styles={{
                                        flexDirection: 'column',
                                        gap: 24,
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <PositionPnLPercentage
                                        color={
                                            Number(position.profitPercentage) > 0
                                                ? colors.alert.green
                                                : colors.alert.red
                                        }
                                    >
                                        {`${
                                            Number(position.profitPercentage) > 0 ? '+' : ''
                                        }${currencyFormatter(
                                            Number(position.profitPercentage),
                                            2,
                                        )}%`}
                                    </PositionPnLPercentage>
                                    <FlexContainer styles={{ gap: 23 }}>
                                        <PricesContainer>
                                            <span>{t('entry_price')}</span>
                                            <span>{position.avgPrice}</span>
                                        </PricesContainer>
                                        <PricesContainer>
                                            <span>
                                                {t(isActive ? 'market_price' : 'exit_price')}
                                            </span>
                                            <span>{isActive ? position.lastPrice : ''}</span>
                                        </PricesContainer>
                                    </FlexContainer>
                                </FlexContainer>
                            </FlexContainer>
                        </PnLAnalysisContainer>
                        <ReferralContainer>
                            <FlexContainer styles={{ width: 266, paddingRight: 12 }}>
                                <FlexContainer
                                    styles={{
                                        gap: 2,
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        marginRight: 30,
                                    }}
                                >
                                    <ReferralDescription>
                                        {t('share_pl_modal_referral_description')}
                                    </ReferralDescription>
                                    <ReferralCode>{`${t('share_pl_modal_referral_code_title')}: ${
                                        userInviteInfo.inviteCode
                                    }`}</ReferralCode>
                                </FlexContainer>
                            </FlexContainer>
                            <FlexContainer>
                                <QRCodeSVG
                                    size={44}
                                    value={`${COINFLARE_REGISTER_URL}/${userInviteInfo.inviteCode}`}
                                />
                            </FlexContainer>
                        </ReferralContainer>
                    </ImageContainer>
                </ModalContent>
            </StyledModal>
        );
    }, [position, open, t, colors, isActive, userInviteInfo]);

    const DrawerComponent = useMemo(() => {
        const handleCancel = () => {
            handleClose();
        };

        const copyReferralCodeLink = (value: string, message: string) => {
            copyToClipboard(value);
            dispatch(
                showNotification({
                    title: message,
                    type: NOTIFICATION_TYPES.SUCCESS as ToastType,
                }),
            );
        };

        return (
            <Drawer isBackdropHidden open handleClose={handleCancel} anchor={'bottom'}>
                <DrawerContainer>
                    <DrawerHeader>
                        <DrawerTitle>{t('share_pl_modal_title_mobile')}</DrawerTitle>
                        <Cancel
                            stroke={colors.text.primary}
                            style={{ cursor: 'pointer' }}
                            onClick={handleCancel}
                        />
                    </DrawerHeader>
                    <SocialMediaContainer>
                        <SocialMediaItem onClick={onSaveImageClick}>
                            <Download
                                fill={colors.background.secondary.v4}
                                stroke={colors.text.primary}
                            />
                            <SocialMediaItemText>{t('save_image')}</SocialMediaItemText>
                        </SocialMediaItem>
                        <SocialMediaItem
                            onClick={() => {
                                copyReferralCodeLink(
                                    `${COINFLARE_REGISTER_URL}/${userInviteInfo.inviteCode}`,
                                    'copied',
                                );
                            }}
                        >
                            <CopyLink
                                fill={colors.background.secondary.v4}
                                stroke={colors.text.primary}
                            />
                            <SocialMediaItemText>{t('copy_link')}</SocialMediaItemText>
                        </SocialMediaItem>
                        <SocialMediaItem
                            onClick={() => {
                                window.open(
                                    `${SharePostLinksEnum.TWITTER}text=${encodeURIComponent(
                                        t('share_pl_modal_referral_description'),
                                    )}&url=${encodeURIComponent(BASE_URL)}`,
                                    '_blank',
                                );
                            }}
                        >
                            <Twitter />
                            <SocialMediaItemText>{t('twitter')}</SocialMediaItemText>
                        </SocialMediaItem>
                        <SocialMediaItem
                            onClick={() => {
                                window.open(
                                    `${SharePostLinksEnum.TELEGRAM}&url=${encodeURIComponent(
                                        BASE_URL,
                                    )}&text=${encodeURIComponent(
                                        t('share_pl_modal_referral_description'),
                                    )}`,
                                    '_blank',
                                );
                            }}
                        >
                            <Telegram />
                            <SocialMediaItemText>{t('telegram')}</SocialMediaItemText>
                        </SocialMediaItem>
                        <SocialMediaItem
                            onClick={() => {
                                window.open(
                                    `${SharePostLinksEnum.WHATSAPP}text=${encodeURIComponent(
                                        t('share_pl_modal_referral_description'),
                                    )} ${encodeURIComponent(BASE_URL)}`,
                                    '_blank',
                                );
                            }}
                        >
                            <Whatsapp />
                            <SocialMediaItemText>{t('whatsapp')}</SocialMediaItemText>
                        </SocialMediaItem>
                        <SocialMediaItem
                            onClick={() => {
                                window.open(
                                    `mailto:?subject=${encodeURIComponent(
                                        'Share',
                                    )}&body=${encodeURIComponent(
                                        t('share_pl_modal_referral_description'),
                                    )}`,
                                    '_blank',
                                );
                            }}
                        >
                            <Email
                                fill={colors.background.secondary.v4}
                                stroke={colors.text.primary}
                            />
                            <SocialMediaItemText>{t('email')}</SocialMediaItemText>
                        </SocialMediaItem>
                        <SocialMediaItem
                            onClick={() => {
                                window.open(
                                    `${SharePostLinksEnum.FACEBOOK}u=${encodeURIComponent(
                                        BASE_URL,
                                    )}`,
                                    '_blank',
                                );
                            }}
                        >
                            <Facebook />
                            <SocialMediaItemText>{t('facebook')}</SocialMediaItemText>
                        </SocialMediaItem>
                        <SocialMediaItem
                            onClick={() => {
                                window.open(
                                    `${SharePostLinksEnum.LINKEDIN}url=${BASE_URL}`,
                                    '_blank',
                                );
                            }}
                        >
                            <Linkedin />
                            <SocialMediaItemText>{t('linkedin')}</SocialMediaItemText>
                        </SocialMediaItem>
                    </SocialMediaContainer>
                </DrawerContainer>
            </Drawer>
        );
    }, [t, colors, onSaveImageClick, handleClose, dispatch, userInviteInfo]);
    return <ModalWithDrawer modal={Modal} drawer={DrawerComponent} open />;
};

export { ModalWithDrawerMobile };
